import { useCallback, useEffect, useRef, useState } from "react";

/**
 * 영문만 받는 입력칸의 공통 규칙 — 계정명(AccountIdField)과 비밀번호(PasswordField)가 함께 쓴다.
 *
 * <h3>왜 '되돌리기' 가 아니라 '막기' 인가</h3>
 * 한글을 눌린 영문 키로 되돌리는 방법도 있지만(ㅏㄴㅈ → ksw), 사용자는 자기가 뭘 쳤는지 모른 채
 * 엉뚱한 글자를 받아든다 — 특히 비밀번호는 화면에 가려져 있어 <b>틀린 걸 확인할 방법이 없다</b>.
 * 그래서 한글은 값에 넣지 않고, 대신 <b>왜 안 들어가는지 화면에 말해 준다</b>.
 *
 * <h3>두 겹으로 막는다</h3>
 * <ol>
 *   <li><b>IME 세션 종료</b> — 조합이 시작되는 순간 input 을 잠깐 readOnly 로 만들면 조합이 끊기고,
 *       다시 열릴 때 입력 모드가 영문으로 돌아간다. 브라우저·OS 가 보장하는 동작은 아니라
 *       (웹 표준에는 IME 를 영문으로 바꾸는 API 가 없다 — {@code ime-mode} 는 폐기됐다)
 *       Chrome 계열에서 잘 듣고 Safari·모바일에서는 안 들을 수 있다.</li>
 *   <li><b>값 정제</b> — 그래서 위가 안 먹거나 붙여넣기로 들어와도, 값 자체에서 허용 밖 문자를 걷어낸다.
 *       이쪽은 우리가 DOM 을 직접 쥐고 있으므로 <b>환경과 무관하게 확실하다</b>.</li>
 * </ol>
 */

/**
 * 걷어낼 문자 — 출력 가능한 ASCII 밖 전부(= 한글·한자·이모지). 계정명과 비밀번호가 같은 규칙을 쓴다.
 *
 * <p>'영문만' 으로 좁히면 안 된다: 비밀번호는 서버 규칙이 숫자와 특수문자(!@#$%^&*())를 <b>요구</b>하므로
 * a-zA-Z 만 남기면 규칙을 만족하는 비밀번호를 아예 칠 수 없다. 공백도 남긴다 —
 * 이미 공백이 든 비밀번호를 쓰는 사람이 있으면 조용히 로그인이 막힌다.
 * 막을 대상은 처음부터 한글 하나뿐이었다.
 */
export const NON_ASCII = /[^\x20-\x7E]/g;

/** 한글(완성형·낱자)이 하나라도 섞였는지 — 안내를 띄울지 판단하는 기준. */
export const hasHangul = (v) => /[ㄱ-ㅎㅏ-ㅣ가-힣]/.test(v || "");

export const ENGLISH_ONLY_NOTICE = "한글은 입력할 수 없습니다. 한/영 키를 눌러 영문으로 입력해 주세요.";

/** 안내 문구가 화면에 머무는 시간(ms). */
const NOTICE_MS = 3000;

/**
 * @param {RegExp} [disallowed=NON_ASCII] 걷어낼 문자 패턴
 * @returns {{notice: string, sanitize: (v: string) => string, clearNotice: () => void, imeProps: object}}
 */
export function useEnglishOnly(disallowed = NON_ASCII) {
    const [notice, setNotice] = useState("");
    const timerRef = useRef(null);

    // 안내가 떠 있는 동안 화면을 떠나면 타이머가 사라진 컴포넌트에 setState 한다.
    useEffect(() => () => clearTimeout(timerRef.current), []);

    const warn = useCallback(() => {
        setNotice(ENGLISH_ONLY_NOTICE);
        clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => setNotice(""), NOTICE_MS);
    }, []);

    const clearNotice = useCallback(() => {
        clearTimeout(timerRef.current);
        setNotice("");
    }, []);

    /**
     * 허용 밖 문자를 걷어낸다.
     *
     * <p>안내는 <b>한글이 섞였을 때만</b> 띄운다. 계정명에서 공백·이모지가 걸러진 것까지
     * "한글은 입력할 수 없습니다" 라고 말하면 화면이 거짓말을 한다.
     */
    const sanitize = useCallback((v) => {
        const raw = v || "";
        const clean = raw.replace(disallowed, "");
        if (clean !== raw && hasHangul(raw)) warn();
        return clean;
    }, [disallowed, warn]);

    /** input 엘리먼트에 그대로 펼쳐 넣는 IME 관련 속성 묶음. */
    const imeProps = {
        lang: "en",
        autoCapitalize: "off",   // 모바일에서 첫 글자만 대문자로 바뀌는 것 방지 — 계정명이 어긋난다
        autoCorrect: "off",
        spellCheck: false,
        /* 한글 조합이 시작되는 바로 그 순간 잡는다. readOnly 로 만들면 조합 중이던 IME 세션이 끊기고,
         * 같은 tick 끝에 되돌리면 입력 모드가 영문으로 재설정된다 — 사용자가 한/영 키를 안 눌러도 된다.
         * (0ms 타이머는 '이벤트 처리 끝난 직후' 를 뜻한다. 이 안에서 el 은 이미 붙잡아 둔 DOM 노드라
         *  React 합성 이벤트가 정리돼도 안전하다.) */
        onCompositionStart: (e) => {
            const el = e.target;
            el.readOnly = true;
            setTimeout(() => { el.readOnly = false; }, 0);
            warn();
        },
    };

    return { notice, sanitize, clearNotice, imeProps };
}
